/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { NotesAiDataService } from '@ffd/api-client';
import { AudioRecorder } from '@libs/speech-recognition';

import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DEFAULT_DEVICE_ID, MediaResourceService, ToastService, VolumeLevelService } from '@libs/shared-services';
import { createClient, DeepgramClient, ListenLiveClient, AgentLiveClient } from '@deepgram/sdk';

type SessionState = 'configure' | 'starting' | 'blocked' | 'error' | 'running';

@Injectable({
  providedIn: 'root',
})
export class PerioTextToSpeechService {
  public readonly sessionState: WritableSignal<SessionState> = signal('configure');
  private readonly notesAIDataService = inject(NotesAiDataService);
  private readonly toastService = inject(ToastService);
  private readonly volumeLevelService = inject(VolumeLevelService);
  readonly #mediaResourceService = inject(MediaResourceService);

  private audio: BlobPart[] = [];
  private client: DeepgramClient | null = null;
  #mediaStream: MediaStream | null = null;
  public connection: ListenLiveClient | AgentLiveClient | null = null;

  public activeDeviceId: WritableSignal<string> = signal(DEFAULT_DEVICE_ID);
  public transcriptionStatus: WritableSignal<string> = signal('configure');
  public keyterms: WritableSignal<string[]> = signal([
    'tooth',
    'teeth',
    'buccal',
    'lingual',
    'palatal',
    'facial',
    'mesial',
    'central',
    'distal',
    'bleeding',
    'suppuration',
    'furcation',
    'recession',
    'mobility',
    'bone loss',
    'pocket',
    'upper',
    'lower',
  ]);
  public ttsMode: WritableSignal<'deepgram' | 'agent'> = signal('deepgram');
  public volumeLevel = this.volumeLevelService.mediaVolumeLevel;
  public isListening: WritableSignal<boolean> = signal(false);

  public userMessage$ = new BehaviorSubject<string | null>(null);

  public nextUnprocessedCommand$ = new BehaviorSubject<string | null>(null);

  private audioRecorder: AudioRecorder | null = null;

  /**
   * Get a copy of the current audio chunks
   * Returns a new array to prevent external modifications
   */
  public getAudioChunks(): BlobPart[] {
    return [...this.audio];
  }

  /**
   * Reset the audio chunks array
   * Should be called when starting a new recording session
   */
  private resetAudioChunks(): void {
    this.audio = [];
  }

  public selectMicrophone(deviceId: string): void {
    this.activeDeviceId.set(deviceId);

    // If transcription is running, restart with the new device
    if (this.transcriptionStatus() === 'running') {
      this.stopTranscription().then(() => {
        setTimeout(() => {
          this.startTranscription();
        }, 500); // Small delay to ensure clean restart
      });
    }
  }

  private subscribeToResults(): void {
    this.connection?.on('error', (error) => {
      console.error(error);
    });

    this.connection?.on('SpeechStarted', () => {
      this.transcriptionStatus.set('running');
    });

    this.connection?.on('Results', (data) => {
      this.transcriptionStatus.set('running');
      const result = data.channel.alternatives[0].transcript;
      // Ignore empty transcripts
      if (result.length === 0 || !data.is_final) {
        if (data.channel.alternatives[0].transcript.length > 0) {
          this.isListening.set(true);
        }

        return;
      }

      this.isListening.set(false);
      this.userMessage$.next(result);
    });

    this.connection?.on('Error', () => {
      this.isListening.set(false);
      this.toastService.error('An error occurred during transcription. Please try again.');
      this.stopTranscription();
    });
  }

  // Call the start method on click to start the websocket
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public readonly startTranscription = async () => {
    this.resetAudioChunks();
    try {
      // Get a fresh token every time we start transcription
      const tokenResponse = await firstValueFrom(this.notesAIDataService.getDeepgramSpeechToken());
      const deepgramToken = tokenResponse?.token;

      if (!deepgramToken) {
        throw new Error('Failed to get Deepgram token');
      }

      return this.startRegularTranscription(deepgramToken);
    } catch (error) {
      console.error('Error getting Deepgram token:', error);
      this.toastService.error('Failed to get authentication token. Please try again.');
      this.sessionState.set('error');
      this.transcriptionStatus.set('error');
      throw error;
    }
  };

  // Regular Deepgram transcription (existing method)
  private readonly startRegularTranscription = async (deepgramToken: string) => {
    this.client = createClient({ accessToken: deepgramToken });

    this.transcriptionStatus.set('starting');
    this.connection?.disconnect();
    this.connection = null;
    this.connection = this.client.listen.live({
      keyterm: this.keyterms(),
      model: 'nova-3',
      language: 'en-US',
      endpointing: 10,
      encoding: 'linear16',
      sample_rate: 48_000,
      interim_results: true,
      vad_events: true,
      punctuate: true,
    });
    this.connection?.on('open', async () => {
      this.audioRecorder = new AudioRecorder(this.sendAudio);
      this.#mediaStream = await this.#mediaResourceService.getMicrophoneStream(this.activeDeviceId());

      this.volumeLevelService.startListenToVolume(this.#mediaStream, false);
      this.audioRecorder?.startRecording(this.activeDeviceId());
      this.subscribeToResults();
    });
  };

  private buffer: Uint8Array = new Uint8Array();

  public combineArray(newData: Uint8Array): void {
    const newBuffer = new Uint8Array(this.buffer.length + newData.length);
    newBuffer.set(this.buffer);
    newBuffer.set(newData, this.buffer.length);
    this.buffer = newBuffer;
  }

  private readonly sendAudio = async (data: Blob) => {
    this.audio.push(data);
    if (data.size > 0 && this.connection?.isConnected()) {
      this.connection?.send(data);
    }
  };

  // Stop the transcription on click to end the recording
  public readonly stopTranscription = async (): Promise<void> => {
    this.connection?.disconnect();
    this.audioRecorder?.stopRecording();
    this.sessionState.set('configure');
    this.transcriptionStatus.set('configure');
    this.isListening.set(false);
    this.#mediaStream?.getTracks().forEach((track) => track.stop());
    this.volumeLevelService.stopListenToVolume();
    this.buffer = new Uint8Array();
    // Reset userMessage$ to prevent replaying old messages when resubscribing
    this.userMessage$.next(null);
  };
}
